/**
 * Self Referral QR Code Component
 *
 * Displays a QR code that links to the self-referral page for the provider.
 * Includes functionality to save the QR code locally.
 *
 * @module components/provider-dashboard/SelfReferralQRCode
 */

import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getLogger } from '@/services';
import type { ProviderQRCodeResponse } from '@/services/domains/SelfReferralDomain';

interface SelfReferralQRCodeProps {
  /** Provider ID for the QR code URL */
  providerId: string;
  /** QR code data from API */
  qrCodeData: ProviderQRCodeResponse | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string;
  /** Custom class name */
  className?: string;
}

/**
 * Self Referral QR Code Component
 */
export const SelfReferralQRCode: React.FC<SelfReferralQRCodeProps> = ({
  providerId,
  qrCodeData,
  loading,
  error,
  className
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();

  /**
   * Handle saving QR code locally
   */
  const handleSaveQRCode = () => {
    if (!qrCodeData?.qr_code_base64) return;

    try {
      // Convert base64 to blob
      const base64Data = qrCodeData.qr_code_base64.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `self-referral-qr-code-${providerId}.png`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to save QR code:', err);
    }
  };

  /**
   * Handle copying URL to clipboard
   */
  const handleCopyUrl = async () => {
    if (!qrCodeData?.static_url) return;

    try {
      await navigator.clipboard.writeText(qrCodeData.static_url);
      // You could add a toast notification here
    } catch (err) {
      logger.error('Failed to copy URL:', err);
    }
  };

  if (error) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="text-center space-y-4">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
            isDark ? 'bg-error-900/20' : 'bg-error-50'
          }`}>
            <svg className={`w-8 h-8 ${
              isDark ? 'text-error-400' : 'text-error-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="space-y-2">
            <BodyText className="font-medium text-error-600">
              QR Code Error
            </BodyText>
            <BodyText variant="muted" size="sm">
              {error}
            </BodyText>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 ${className}`}>
      <div className="text-center space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <TitleText level={4} className="font-semibold">
            Self-Referral
          </TitleText>
        </div>

        {/* QR Code Display */}
        <div className="flex flex-col items-center space-y-4">
          {loading ? (
            <div className="w-[200px] h-[200px] flex items-center justify-center border-2 border-dashed border-neutral-300 rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : qrCodeData ? (
            <div className="relative">
              <img
                src={qrCodeData.qr_code_base64}
                alt="Self-referral QR Code"
                className="w-[200px] h-[200px] rounded-lg border border-neutral-200"
              />
            </div>
          ) : null}
        </div>

        {/* URL Display */}
        {qrCodeData && (
          <div className={`p-3 rounded-lg text-left ${
            isDark ? 'bg-secondary-800' : 'bg-neutral-100'
          }`}>
            <BodyText variant="secondary" size="sm" className="font-medium mb-1">
              Referral URL:
            </BodyText>
            <a
              href={qrCodeData.static_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`break-all font-mono text-sm hover:underline ${
                isDark ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'
              }`}
            >
              {qrCodeData.static_url}
            </a>
          </div>
        )}

        {/* Action Buttons */}
        {qrCodeData && (
          <div className="space-y-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveQRCode}
              disabled={loading}
              className="w-full"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Save QR Code
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyUrl}
              disabled={loading}
              className="w-full"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy URL
            </Button>
          </div>
        )}

        {/* Info */}
        {qrCodeData && (
          <div className={`p-3 rounded-lg ${
            isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
          } border`}>
            <BodyText size="sm" className={isDark ? 'text-blue-300' : 'text-blue-700'}>
              {qrCodeData.instructions.patient_instructions}
            </BodyText>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SelfReferralQRCode;